import type { Locale } from "@/i18n/locales";

// Number formatting is bound to the SELECTED locale, never the browser's.
//
// Before this module, `fmtUSD` and CountUp's default both called
// `toLocaleString()` with no argument, which resolves against the visitor's
// browser locale. That was invisible while the site was English-only; with a
// language switcher it becomes a visible contradiction — a reader picks Deutsch
// and the figures still follow whatever their browser happens to be set to, so
// two people on the same German page can see "$200,000" and "$200.000".
//
// The BCP-47 tags are derived from the locale set rather than hardcoded, so a
// new locale gets correct grouping without a second place to edit.
const NUMBER_LOCALE: Readonly<Record<Locale, string>> = {
  en: "en-US",
  de: "de-DE",
};

export function formatNumber(value: number, locale: Locale): string {
  return value.toLocaleString(NUMBER_LOCALE[locale]);
}

/**
 * USD amounts keep the "$" prefix in every locale: the account sizes, fees and
 * targets are quoted in dollars and the currency does not change with the
 * reader's language. Only the GROUPING does — "200,000" vs "200.000".
 */
export function formatUsd(value: number, locale: Locale): string {
  return `$${formatNumber(value, locale)}`;
}

/** Percent, with the space German typography requires and English omits. */
export function formatPercent(value: number, locale: Locale): string {
  return locale === "de" ? `${Math.round(value)} %` : `${Math.round(value)}%`;
}

/** A day count: "7d" in English, "7 T" in German. */
export function formatDays(value: number, locale: Locale): string {
  return locale === "de" ? `${Math.round(value)} T` : `${Math.round(value)}d`;
}
